<template>
    <div>
        <b-breadcrumb class="text-capitalize">
            <b-breadcrumb-item :to="{ name: 'AthleteList' }"> <font-awesome-icon icon="caret-right" class="mr-1" />Athletes</b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'AthleteList' }">{{athleteName.truncate(32)}}</b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'SessionList' }">Sessions</b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'Sessions' }" active>{{sessionName.truncate(32)}}</b-breadcrumb-item>
        </b-breadcrumb>


            <ErrorView :visible="isLoadingError" :errorValue="loadingErrorValue"/>

            <div v-if="isLoadingError == false" class="mx-4">

              <b-skeleton-wrapper :loading="isLoading">
                <template #loading>
                  <b-card>
                    <b-skeleton width="80%"></b-skeleton>
                    <b-skeleton width="60%"></b-skeleton>
                    <b-skeleton width="40%"></b-skeleton>
                    <b-skeleton width="45%"></b-skeleton>
                    <b-skeleton width="20%"></b-skeleton>
                  </b-card>
                </template>

                <b-card class="p-1" body-class="pb-0 pt-0">
                  <div class="container-fluid px-1 py-0">
                    <div class="row py-0 my-0">
                      <div class="col-6 px-1 py-0 my-0">
                        <b-card-title>
                          <SessionIcon class="ml-0 mr-1" :session="session"/>
                          <div style="display: inline-block; vertical-align: middle;">
                            <span>{{ sessionName }}</span>
                            <b-button size="sm" class="mx-2" style="border-width: 0px" variant="outline-secondary" v-on:click.stop="onEditSessionName()">
                              <font-awesome-icon icon="edit" />
                            </b-button>
                          </div>
                        </b-card-title>
                        <b-card-sub-title class="mb-3">
                              <span class="h5">
                                <font-awesome-icon icon="calendar-alt" class="ml-0 mr-1" style="font-size:19px"/>
                                {{ startTime | moment("MMMM Do, YYYY, h:mm a")}}
                              </span>
                        </b-card-sub-title>
                      </div>
                      <div class="col-sm-3">
                        <SessionStatus :state="sessionState"/>
                        <h4><font-awesome-icon icon="stopwatch" style="margin-right: 12px" />
                          <Transition mode="out-in" name="fade-in">
                            <span :key="elapsedSeconds">{{ elapsedSeconds | numeralFormat('00:00:00') }}</span>
                          </transition>
                        </h4>
                      </div>
                      <div v-if="sessionNavigation.hasCurrent" class="col-md-3 d-none d-md-block">
                        <div class="text-right">
                          <span>{{sessionNavigation.current}} of {{sessionNavigation.total}}</span>
                          <b-button-group>
                            <b-button size="sm" variant="link"
                                      :disabled="!sessionNavigation.hasPrevious"
                                      v-on:click.stop="selectSessionById(sessionNavigation.previousId)">
                              <font-awesome-icon icon="chevron-left" />
                            </b-button>
                            <b-button size="sm" variant="link"
                                      :disabled="!sessionNavigation.hasNext"
                                      v-on:click.stop="selectSessionById(sessionNavigation.nextId)">
                              <font-awesome-icon icon="chevron-right" />
                            </b-button>
                          </b-button-group>
                        </div>
                      </div>
                    </div>

                      <div class="row py-1 my-0">
                        <div class="col px-0 py-0 my-0">
                          <b-input-group size="sm" class="mt-1 px-0 py-0 my-0" >
                            <b-input-group-prepend>
                              <b-button size="sm" variant="outline-secondary" v-on:click.stop="refreshSession(true)">
                                <span><font-awesome-icon icon="sync-alt" class="mr-1"/>Refresh</span>
                              </b-button>
                              <b-dropdown size="sm" variant="outline-secondary" dropdown >
                                <b-dropdown-item-button variant="outline-danger" v-on:click.stop="onDelete()">
                                  <span><font-awesome-icon icon="trash" class="mr-1" />Delete Session</span>
                                </b-dropdown-item-button>
                              </b-dropdown>
                            </b-input-group-prepend>
                          </b-input-group>
                        </div>
                      </div>

                  </div>
                </b-card>

              </b-skeleton-wrapper>

              <b-overlay :show="isLoading" rounded="sm">

                <b-card class="my-2 px-1 py-1" no-body>
                  <b-tabs card bg-variant="dark" class="m-0" >
                      <b-tab title="Details" lazy>
                          <SessionDetailsTab :athlete="athlete" :session="session" :elapsedSeconds="elapsedSeconds"/>
                      </b-tab>
                      <b-tab title="Analysis" lazy>
                          <MeasurementChartsTab />
                      </b-tab>
                      <b-tab title="Timeline & Marks" lazy>
                        <SessionTimelineTab />
                      </b-tab>
                      <b-tab title="Data Tables" lazy>
                          <MeasurementDataTableTab/>
                      </b-tab>
                      <b-tab title="Reports" lazy>
                          <SessionReportTab :measurementData="measurementData" :measurementsExist="measurementsExist"/>
                      </b-tab>
                  </b-tabs>
                </b-card>

              </b-overlay>
            </div>

        <SessionModal/>
        <SessionNotesModal/>
        <SessionDeleteModal :navigateBack="true"/>

    </div>
</template>

<script>

    import { mapActions, mapGetters } from 'vuex'

    import api from '../../api/vo2api'
    import SessionDetailsTab from "./SessionDetailsTab"
    import MeasurementDataTableTab from './MeasurementDataTableTab.vue'
    import MeasurementChartsTab from "./MeasurementChartsTab"
    import SessionTimelineTab from "./SessionTimelineTab"
    import SessionReportTab from "./SessionReportTab"
    import SessionStatus from "./SessionStatus"
    import SessionIcon from "./SessionIcon"
    import SessionModal from "./SessionModal"
    import SessionNotesModal from "./SessionNotesModal"
    import ErrorView from '../ErrorView'
    import SessionDeleteModal from "./SessionDeleteModal";


    export default {
        name: "SessionDetailsPage",
        components: {
            SessionDetailsTab,
            MeasurementChartsTab,
            MeasurementDataTableTab,
            SessionReportTab,
            SessionTimelineTab,
            SessionStatus,
            SessionIcon,
            SessionModal,
            SessionNotesModal,
            SessionDeleteModal,
            ErrorView
        },

        data () {
            return {
                organizationId: this.$route.params.orgId,
                athleteId: this.$route.params.athleteId,
                sessionId: this.$route.params.sessionId,
            }
        },
        watch: {
            session() {
                this.refreshMeasurement();
            },
            sessionId(newValue, oldValue) {
              if(newValue && oldValue) {
                  this.refreshSession();
              }
            },
        },
        computed: {

            ...mapGetters({

                athlete: 'athlete/current',
                athleteName: 'athlete/currentName',

                sessionNavigation: 'session/itemsNavigation',
                session: 'session/current',
                sessionLoading: 'session/currentLoading',
                sessionName: 'session/currentName',
                sessionLoadingError: 'session/currentLoadingError',
                sessionLoadingErrorValue: 'session/currentLoadingErrorValue',

                measurementDataLoading: 'measurement/isLoading',
                measurementData: 'measurement/current',
            }),
            isLoading: function() {
                return this.sessionLoading;
            },
            isLoadingError: function() {
                return this.sessionLoadingError || (!this.isLoading && !this.session);
            },
            loadingErrorValue: function() {
                return this.sessionLoadingErrorValue;
            },

            sessionState: function () {
                if(this.session) {
                    return this.session.state
                }
                return '';
            },

            startTime: function() {
                if(this.session) {
                    return this.session.startTime
                }

                return '';
            },

            elapsedSeconds: function () {
              if(this.measurementData && this.measurementData.elapsedSeconds) {
                return this.measurementData.elapsedSeconds;
              }
              return 0
            },
            measurementsExist: function() {
                if(this.measurementData && this.measurementData.data.find(x => x.points && x.points.length > 0)) {
                   return true;
                } else {
                    return false;
                }
            },

        },
        created () {
            this.refreshOrganization();
            this.refreshAthlete();
            this.refreshSession();
            if(this.session) {
                this.refreshMeasurement();
            }

            //watch session and measurement changes
            this.$sessionHub.sessionOpened(this.sessionId);
            this.$sessionHub.$on('session-measurement-changed', this.onSessionMeasurementChanged);
            this.$sessionHub.$on('session-changed', this.onSessionChanged);
        },
        beforeDestroy () {
            // Notify the server we stopped watching the question
            this.$sessionHub.$off('session-measurement-changed', this.onSessionMeasurementChanged);
            this.$sessionHub.$off('session-changed', this.onSessionChanged);
            this.$sessionHub.sessionClosed(this.sessionId);
        },
        methods: {
            ...mapActions({
                selectOrganization: 'organization/select',
                selectAthlete: 'athlete/select',
                selectSession: 'session/select',

                markSessionDirty: 'session/markDirty',
                replaceSession: 'session/replace',

                updateMeasurement: 'measurement/update',

            }),
            refreshOrganization() {
                this.selectOrganization(this.organizationId)
            },
            refreshAthlete() {
                this.selectAthlete(this.athleteId)
            },
            refreshSession(force) {
                if(force) {
                  this.markSessionDirty();
                }
                this.selectSession(this.sessionId, true);
            },
            refreshMeasurement() {
                api.getMeasurements(this.organizationId, this.sessionId).then(res => {
                    this.processMeasurementData(res);
                })
            },
            selectSessionById(sessionId) {
                if(sessionId) {
                  this.sessionId = sessionId;
                }
            },
            // This is called from the server through SignalR
            onSessionChanged({sessionMeta}) {
                if (!sessionMeta || this.sessionId != sessionMeta.sessionId)
                    return;

                this.replaceSession( { id: sessionMeta.sessionId, model: sessionMeta });
            },
            onSessionMeasurementChanged({measurementData}) {
                if (!measurementData || this.sessionId != measurementData.sessionId)
                    return;

                this.processMeasurementData(measurementData)
            },
            processMeasurementData(measurementData) {
                this.updateMeasurement(measurementData);
            },

            onEditSessionName() {
                this.$bvModal.show('edit-session-modal')
            },

            onDelete() {
              this.$bvModal.show('delete-session-modal')
            },
        },
    }
</script>

<style scoped>


.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}

</style>